import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import DiamondRounded from '@mui/icons-material/DiamondRounded';
import InsightsRounded from '@mui/icons-material/InsightsRounded';
import BedRounded from '@mui/icons-material/BedRounded';
import ViewTimelineRounded from '@mui/icons-material/ViewTimelineRounded';
import TranslateRounded from '@mui/icons-material/TranslateRounded';
import { useTranslation } from 'react-i18next';

import Framed from './Framed';
import { azure, glass, line, slate } from '../theme';
import goldDesk from '../assets/img/nizro/gold-desk.webp';
import insight from '../assets/img/nizro/insight.webp';
import hotelRooms from '../assets/img/nizro/hotel-rooms.webp';
import hotelTimeline from '../assets/img/nizro/hotel-timeline.webp';

/**
 * Real Nizro screens, cropped to one 16:10 shape so swapping them never moves
 * the page. `ui` is the language the screen itself is in, which the caption
 * states: two English screens and two Arabic ones is the bilingual claim made
 * visible. `focus` is what the frame keeps if it has to trim a few pixels.
 */
export const SHOTS = [
  { key: 'gold', src: goldDesk, ui: 'en', Icon: DiamondRounded, focus: 'right top' },
  { key: 'insight', src: insight, ui: 'en', Icon: InsightsRounded, focus: 'top' },
  { key: 'rooms', src: hotelRooms, ui: 'ar', Icon: BedRounded, focus: 'top' },
  { key: 'timeline', src: hotelTimeline, ui: 'ar', Icon: ViewTimelineRounded, focus: 'top' },
];

export const SHOT_RATIO = 1.6;

/** The screen a visitor sees first: an Arabic one on the Arabic site. */
export const leadShot = (lang) => SHOTS.find((s) => s.key === (lang === 'ar' ? 'rooms' : 'gold'));

/** Tabs over a framed screenshot, with a caption saying what it shows. */
export default function NizroShowcase() {
  const { t, i18n } = useTranslation();
  const rtl = i18n.dir() === 'rtl';
  const [active, setActive] = React.useState(() => leadShot(i18n.language).key);
  const tabs = React.useRef({});
  const shot = SHOTS.find((s) => s.key === active);

  // Arrow keys move between tabs (mirrored in Arabic), as the ARIA tabs
  // pattern expects; only the selected tab is in the Tab order.
  const onKeyDown = (event) => {
    const i = SHOTS.findIndex((s) => s.key === active);
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];
    let next;
    if (step) next = (i + step + SHOTS.length) % SHOTS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = SHOTS.length - 1;
    else return;
    event.preventDefault();
    setActive(SHOTS[next].key);
    tabs.current[SHOTS[next].key]?.focus();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: { xs: '18% -4% 0', md: '14% 6% -6%' },
          background: `radial-gradient(55% 55% at 50% 45%, ${alpha(azure[500], 0.3)}, transparent 72%)`,
          filter: 'blur(36px)',
          pointerEvents: 'none',
        }}
      />

      <Stack direction="row" justifyContent="center" sx={{ position: 'relative', mb: { xs: 2.5, md: 3 } }}>
        <Box
          role="tablist"
          aria-label={t('nizro.showcase.label')}
          onKeyDown={onKeyDown}
          sx={{
            display: 'inline-flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 0.5,
            p: 0.5,
            borderRadius: { xs: '22px', sm: 999 },
            border: `1px solid ${line}`,
            bgcolor: glass,
            backdropFilter: 'blur(8px)',
          }}
        >
          {SHOTS.map(({ key, Icon }) => {
            const on = key === active;
            return (
              <ButtonBase
                key={key}
                ref={(el) => {
                  tabs.current[key] = el;
                }}
                role="tab"
                id={`nizro-tab-${key}`}
                aria-selected={on}
                aria-controls="nizro-shot"
                tabIndex={on ? 0 : -1}
                onClick={() => setActive(key)}
                sx={{
                  gap: 0.75,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.9,
                  borderRadius: 999,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: on ? '#04121F' : 'text.secondary',
                  bgcolor: on ? azure[300] : 'transparent',
                  transition: 'color .2s ease, background-color .2s ease',
                  '&:hover': { color: on ? '#04121F' : 'text.primary' },
                  '&.Mui-focusVisible': { outline: `2px solid ${azure[300]}`, outlineOffset: 2 },
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
                {t(`nizro.showcase.${key}.tab`)}
              </ButtonBase>
            );
          })}
        </Box>
      </Stack>

      <Box sx={{ position: 'relative', maxWidth: 1080, mx: 'auto' }}>
        <Framed url="nizro.app" ratio={SHOT_RATIO}>
          <Box
            role="tabpanel"
            id="nizro-shot"
            aria-labelledby={`nizro-tab-${active}`}
            sx={{ position: 'absolute', inset: 0 }}
          >
            {/* All four stay mounted and crossfade, so a click never shows a
                blank frame while the next image loads. */}
            {SHOTS.map(({ key, src, focus }) => {
              const on = key === active;
              return (
                <Box
                  key={key}
                  component="img"
                  src={src}
                  alt={on ? t(`nizro.showcase.${key}.alt`) : ''}
                  aria-hidden={!on}
                  decoding="async"
                  style={{ objectPosition: focus }}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: on ? 1 : 0,
                    transition: 'opacity .35s ease',
                  }}
                />
              );
            })}
          </Box>
        </Framed>
      </Box>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1, sm: 1.5 }}
        alignItems="center"
        justifyContent="center"
        sx={{ position: 'relative', mt: 2.5, textAlign: 'center' }}
      >
        <Typography variant="body2" aria-live="polite" sx={{ color: slate[300], maxWidth: 640 }}>
          {t(`nizro.showcase.${active}.caption`)}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            flex: 'none',
            px: 1.25,
            py: 0.25,
            borderRadius: 999,
            border: `1px solid ${line}`,
            color: 'text.secondary',
          }}
        >
          <TranslateRounded sx={{ fontSize: 14 }} />
          <Typography component="span" variant="caption" sx={{ fontWeight: 600 }}>
            {t(shot.ui === 'ar' ? 'nizro.showcase.inArabic' : 'nizro.showcase.inEnglish')}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
